import nineMarkers from './questions/nine-marker.json';
import twentyMarkers2023 from './questions/twenty-marker/twenty-marker-2023.json';
import twentyMarkers2024 from './questions/twenty-marker/twenty-marker-2024.json';
import mcqResourceSecurity from './questions/multiple-choice/mcq-resource-security.json';
import envSciCardManifest from './questions/a-level/env-science/_question-cards-manifest.json';
import gcseSciCardManifest from './questions/gcse/_gcse-science-question-cards-manifest.json';
import gcseSciFiguresManifest from './questions/gcse/_gcse-science-figures-manifest.json';

// Automatically loads all .json files under src/data/questions/four-marker/**
// and src/data/questions/six-marker/**.
// Any new file added to either directory tree is picked up without editing this file.
const fourMarkerCtx = require.context('./questions/four-marker', true, /\.json$/);
const sixMarkerCtx = require.context('./questions/six-marker', true, /\.json$/);
const gcseCtx = require.context('./questions/gcse', true, /\.json$/);
// New GCSE Science pipeline output: per-paper `-paper.json` files carry a
// `questions` array (same shape as the legacy `-questions.json`) plus a
// `groups` array describing how leaves are bundled into study cards. The
// gcseCtx loader picks these up too, but the leaf-question filter
// (`question_text !== undefined`) drops them as objects; they're consumed
// separately via gcsePaperCtx to build the question-card / alternate-figure
// index.
const gcsePaperCtx = require.context('./questions/gcse', true, /-paper\.json$/);
const envSciCtx = require.context('./questions/a-level/env-science/questions', true, /\.json$/);
const geographyP2Ctx = require.context('./questions/a-level/geography/questions', true, /\.json$/);

function loadContext(ctx) {
  return ctx
    .keys()
    .sort()
    .flatMap((key) => {
      const mod = ctx(key);
      return Array.isArray(mod) ? mod : [mod];
    });
}

// Topics not yet implemented — excluded until student preferences are added
const EXCLUDED_TWENTY_MARKER_TOPICS = new Set(['urban', 'population']);

// Newly extracted A-Level Geography Paper 2 questions use the env-sci-style
// schema (spec_refs / ao_split / mark_points) instead of the topic/archetype
// fields the sidebar and answer panel expect. Surface only the six-markers,
// mapping their schema onto the existing shape. Figures (resource inserts) are
// added manually later, so questions are shown without a resource for now.
// 3.2.3 (urban) and 3.2.4 (population) are omitted to match the existing
// human-geography topics; their absence mirrors the Paper 2 four-markers.
const GEO_SPEC_TO_TOPIC = {
  '3.2.1': 'global_systems',
  '3.2.2': 'changing_places',
  '3.2.5': 'resource_security',
};

function loadGeographyP2SixMarkers(rows) {
  return rows
    .filter((q) => q.marks === 6)
    .map((q) => {
      const section = (q.spec_refs?.[0] ?? '').split('.').slice(0, 3).join('.');
      const topic = GEO_SPEC_TO_TOPIC[section];
      if (!topic) return null;
      // AO3-only questions are data-analysis (data_6); AO1/AO2 ones are evaluative (assess_6).
      const archetype = (q.ao_split?.ao3 ?? 0) === q.marks ? 'data_6' : 'assess_6';
      // The examiner prompt reads `ao_weightings` and a top-level
      // `indicative_content` array. This extraction stores AOs under `ao_split`,
      // and the data questions nest their indicative content inside the
      // per-level descriptors — surface both so the marker isn't sent an empty
      // mark scheme.
      const perLevelIndicative = (q.level_descriptors ?? []).flatMap(
        (lvl) => lvl.indicative_content ?? [],
      );
      // The examiner prompt's resource section reads a top-level
      // `resource.description`, but the multi-resource shape stores text per
      // figure. Synthesise the top-level description from the figures so data
      // questions ("Analyse the data shown in Figure …") aren't sent an empty
      // resource. The figures array is left intact for the on-screen viewer.
      const resource = q.resource
        ? {
            ...q.resource,
            description:
              q.resource.description ??
              (q.resource.figures ?? [])
                .map((f) => f.description ?? f.alt_text)
                .filter(Boolean)
                .join('\n'),
          }
        : q.resource;
      return {
        ...q,
        topic,
        archetype,
        paper: 2,
        figure_required: q.resource_insert_required ?? false,
        ao_weightings: q.ao_split,
        indicative_content: [...(q.indicative_content ?? []), ...perLevelIndicative],
        resource,
      };
    })
    .filter(Boolean);
}

// A-Level Geography questions (existing)
export const questions = [
  ...loadContext(fourMarkerCtx),
  ...loadContext(sixMarkerCtx),
  ...loadGeographyP2SixMarkers(loadContext(geographyP2Ctx)),
  ...nineMarkers,
  ...[...twentyMarkers2023, ...twentyMarkers2024].filter(
    (q) => !EXCLUDED_TWENTY_MARKER_TOPICS.has(q.topic),
  ),
  ...mcqResourceSecurity.questions,
];

// A-Level Environmental Science questions
// Exclude resource-only entries (no question_text) and MCQ-style single-answer
// questions that are better served by the MCQ quiz surface.
const RAW_ENV_SCI = loadContext(envSciCtx)
  .filter((q) => q.question_text !== undefined);

// Assign archetype based on marks and mark_scheme_type
function assignEnvSciArchetype(q) {
  if (q.mark_scheme_type === 'level_based') {
    if (q.marks >= 20) return 'envsci_25';
    return 'envsci_9';
  }
  return 'envsci_short';
}

// Env-science papers store visuals on the first subpart (e.g. q01.1) and later
// subparts reference the same figure. Inherit from a sibling subpart whose
// stem (the digits before the first dot) matches and which carries visuals.
function envSciStemKey(q) {
  const dot = q.question_number?.indexOf('.');
  return `${q.source_paper}|${dot > 0 ? q.question_number.slice(0, dot) : q.question_number}`;
}

function rewriteEnvSciPath(filePath, sourcePaper) {
  // Source paths look like "visuals/AQA-74471-JUN22/q01-1-table-a.png".
  // Pipeline copies PNGs to /public/images/figures/envsci/<paper>/...
  if (!filePath) return filePath;
  const base = filePath.split('/').pop();
  return `/images/figures/envsci/${sourcePaper}/${base}`;
}

function envSciVisualsToResource(visuals, sourcePaper, questionNumber) {
  // Some env-science visuals have caption + alt_text but no `file_path` (no PNG
  // was extracted). Keep them in `visuals[]` so the server prompt still gets
  // the description, but only include displayable figures in resource.figures.
  const displayable = (visuals ?? []).filter((v) => v.file_path);
  if (!displayable.length) return null;
  return {
    id:   `${sourcePaper}-${questionNumber}`,
    type: 'multi-resource',
    figures: displayable.map((v) => ({
      fig_id:      v.visual_id,
      file_path:   rewriteEnvSciPath(v.file_path, sourcePaper),
      alt_text:    v.alt_text ?? '',
      description: v.caption ?? v.alt_text ?? '',
    })),
  };
}

function normalizeEnvSciVisuals(rows) {
  // 1. Map first-subpart visuals so later subparts in the same stem can inherit.
  const stemVisuals = new Map();
  for (const q of rows) {
    if (q.visuals?.length) {
      const k = envSciStemKey(q);
      if (!stemVisuals.has(k)) stemVisuals.set(k, q.visuals);
    }
  }

  return rows.map((q) => {
    const ownVisuals = q.visuals?.length ? q.visuals : [];
    const inherited  = ownVisuals.length
      ? ownVisuals
      : (q.figure_required ? (stemVisuals.get(envSciStemKey(q)) ?? []) : []);
    if (!inherited.length) return q;
    const resource = envSciVisualsToResource(inherited, q.source_paper, q.question_number);
    const next = { ...q, visuals: inherited };
    if (resource) next.resource = resource;
    return next;
  });
}

// The env-science extraction pipeline currently produces unreliable figure
// alignments (wrong figure attached to wrong question, missing PNGs, etc.).
// Until that's fixed, drop any question that references a figure — and strip
// `visuals`/`resource` from the survivors so no stale image renders.
const FIG_REF_RE = /\b(figure|fig\.?|table|map|graph|diagram|photograph|chart|image)\s+\d/i;
function envSciReferencesFigure(q) {
  if (q.figure_required === true) return true;
  return FIG_REF_RE.test([q.question_text, q.stimulus_material].filter(Boolean).join(' '));
}

// Reverse-chronological sort key for AQA paper codes like "AQA-74471-JUN22"
// or "AQA-74471-NOV21". Higher = later. Anything without a recognised
// month/year (e.g. "AQA-74471-SAMPLE") returns -Infinity so it sorts last
// under DESC ordering.
const PAPER_MONTHS = { JAN: 1, FEB: 2, MAR: 3, APR: 4, MAY: 5, JUN: 6, JUL: 7, AUG: 8, SEP: 9, OCT: 10, NOV: 11, DEC: 12 };
function paperChronoKey(sourcePaper) {
  const m = /(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(\d{2})/.exec(sourcePaper ?? '');
  if (!m) return -Infinity;
  return (2000 + parseInt(m[2], 10)) * 100 + PAPER_MONTHS[m[1]];
}

// Pre-rendered question-card screenshots manifest (paper, question_number, file).
// Generated by pipeline/copy-envsci-question-cards.js on prestart/prebuild and
// imported at the top of this module.
const envSciCardMap = new Map(
  (envSciCardManifest?.cards ?? []).map((c) => [`${c.paper}|${c.question_number}`, c.file]),
);

function lookupEnvSciCard(q) {
  const own = envSciCardMap.get(`${q.source_paper}|${q.question_number}`);
  if (own) return own;
  // Stem fallback: e.g. card "03" covers questions "03.1", "03.2".
  const dot = q.question_number?.indexOf('.');
  if (dot > 0) {
    const stem = q.question_number.slice(0, dot);
    return envSciCardMap.get(`${q.source_paper}|${stem}`) ?? null;
  }
  return null;
}

// Attach question-card images to env-science questions. The card image is the
// authoritative visual rendering (cropped from the PDF) so it replaces the
// unreliable per-figure extraction. Strip any stale visuals/resource that the
// previous extraction step may have attached.
const ENV_SCI_NORMALIZED = normalizeEnvSciVisuals(RAW_ENV_SCI)
  .map(({ visuals, resource, ...rest }) => {
    const card = lookupEnvSciCard(rest);
    if (!card) return rest;
    return {
      ...rest,
      question_card_image: card,
      // Synthesise a resource so the existing ResourceViewer / MockExamWizard
      // / FeedbackTerminal flow renders the card image in the right panel
      // without needing component changes.
      resource: {
        id:   `${rest.source_paper}-${rest.question_number}-card`,
        type: 'multi-resource',
        figures: [
          {
            fig_id:           'question-card',
            file_path:        card,
            alt_text:         rest.question_text ?? '',
            description:      '',
            is_question_card: true,
          },
        ],
      },
    };
  })
  // Keep questions that either don't reference a figure OR have a card image
  // available — the card image makes a figure-referencing question answerable
  // again. The remaining no-card figure-referencing questions stay hidden.
  .filter((q) => !envSciReferencesFigure(q) || q.question_card_image);

export const envSciQuestions = ENV_SCI_NORMALIZED
  .map((q) => ({
    ...q,
    subject:      'environmental-science',
    examLevel:    'a-level',
    archetype:    assignEnvSciArchetype(q),
    // Derive paper number from the AQA spec code: 74471 → 1, 74472 → 2.
    paper_number: Number((q.source_paper ?? '').match(/AQA-7447(\d)/)?.[1]) || 1,
    topic:        q.spec_refs?.[0]?.split('.').slice(0, 3).join('.') ?? q.source_paper ?? 'env-science',
  }))
  // Reverse chronological by paper (sample sets last). Within a paper, keep
  // the natural question_number order (01.1, 01.2, 02.1 …) by using a stable
  // sort and tie-breaking on question_number.
  .sort((a, b) => {
    const keyDiff = paperChronoKey(b.source_paper) - paperChronoKey(a.source_paper);
    if (keyDiff !== 0) return keyDiff;
    return (a.question_number ?? '').localeCompare(b.question_number ?? '', undefined, { numeric: true });
  });

// All GCSE questions — exclude fact cards (no question_text) and other non-question assets
const allGcseQuestions = loadContext(gcseCtx).filter((q) => q.question_text !== undefined);

// GCSE Science papers store visuals on the parent "stem" row (marks: 0) and on
// individual subparts. Subparts inherit any visuals from their stem. Then drop
// the marks-0 stems and convert the `visuals` array into the `resource.figures`
// shape that ResourceViewer/App.js already use for A-Level Geography.
const SCIENCE_SUBJECTS = new Set(['biology', 'chemistry', 'physics']);

function stemKey(q) {
  // "01.2" -> "01"; "01" -> "01"
  const dot = q.question_number?.indexOf('.');
  return `${q.source_paper}|${dot > 0 ? q.question_number.slice(0, dot) : q.question_number}`;
}

function rewriteSciencePath(filePath, subject, sourcePaper) {
  // Source JSON paths look like "visuals/biology/AQA-84641B1H/q01-fig-a.png".
  // Pipeline copies PNGs to /public/images/figures/gcse/<subject>/<paper>/...
  if (!filePath) return filePath;
  const base = filePath.split('/').pop();
  return `/images/figures/gcse/${subject}/${sourcePaper}/${base}`;
}

function visualsToResource(visuals, subject, sourcePaper, questionNumber) {
  if (!visuals?.length) return null;
  return {
    id:   `${sourcePaper}-${questionNumber}`,
    type: 'multi-resource',
    figures: visuals.map((v) => ({
      fig_id:      v.visual_id,
      file_path:   rewriteSciencePath(v.file_path, subject, sourcePaper),
      alt_text:    v.alt_text ?? '',
      description: v.caption ?? v.alt_text ?? '',
    })),
  };
}

// Map AQA science archetypes (structured, short_answer, calculation, mcq,
// complete_figure, practical_method, extended_6mark) onto the two UI archetypes
// the AnswerPanel knows about. Original AQA archetype is preserved on
// science_archetype so the server-side marker can choose marking rubrics.
function uiArchetypeFor(scienceArchetype) {
  return scienceArchetype === 'extended_6mark' ? 'gcse_science_extended_6' : 'gcse_science_answer';
}

function normalizeScienceQuestions(rows) {
  // 1. Build a stem -> visuals map for parents that carry shared figures.
  const stemVisuals = new Map();
  for (const q of rows) {
    if (q.marks === 0 && q.visuals?.length) {
      stemVisuals.set(stemKey(q), q.visuals);
    }
  }

  // 2. Drop stems, attach inherited visuals, synthesize resource, remap archetype.
  return rows
    .filter((q) => q.marks > 0)
    .map((q) => {
      const ownVisuals = q.visuals?.length ? q.visuals : [];
      // Only inherit the parent stem's visuals when the subpart actually requires the figure
      const inherited = ownVisuals.length
        ? ownVisuals
        : (q.figure_required ? (stemVisuals.get(stemKey(q)) ?? []) : []);
      const resource  = visualsToResource(inherited, q.subject, q.source_paper, q.question_number);
      const remapped  = {
        ...q,
        examLevel:         'gcse',
        science_archetype: q.archetype,
        archetype:         uiArchetypeFor(q.archetype),
      };
      if (inherited.length) {
        remapped.visuals = inherited;
        remapped.resource = resource;
      }
      return remapped;
    });
}

// ─── GCSE Science groups + question-card index ──────────────────────────────
// The new `-paper.json` extraction shape bundles related leaves into
// `QuestionGroup`s, each with a pre-rendered card PNG and a list of
// "alternate" visuals from sibling subparts that share the same parent. The
// loader exposes these as a single resource per leaf: the group card is the
// primary figure, alternates follow so the student can swipe to sibling
// figures from the same question without losing context.

const gcseScienceCardMap = new Map(
  (gcseSciCardManifest?.cards ?? []).map((c) => [`${c.subject}|${c.paper}|${c.group_id}`, c.file]),
);

// Set of "<subject>|<paper>|<crop-basename>" entries the pipeline actually
// copied to public/. The Biology Jun-2018 papers ship cards but not figure
// crops, so an alternate_visual_id referencing a missing crop would render as
// a broken image in the right-pane swipe. Gating on this set lets the cards
// render while silently dropping unresolvable alternates until the pipeline
// emits the crops.
const gcseScienceFigureSet = new Set(
  (gcseSciFiguresManifest?.figures ?? []).map((f) => `${f.subject}|${f.paper}|${f.basename}`),
);

function buildSciencePaperIndex(paperCtx) {
  // questionId -> { primaryFigure, alternateFigures[], groupId }
  const leafIndex = new Map();

  for (const key of paperCtx.keys()) {
    const paper = paperCtx(key);
    if (!paper || !Array.isArray(paper.groups) || !Array.isArray(paper.questions)) continue;

    // Paper-wide visual_id -> figure descriptor (visuals are spread across
    // leaves; build the lookup once per paper so alternate_visual_ids can
    // resolve regardless of which subpart originally carried the figure).
    // Skip visuals whose underlying PNG wasn't copied to public/ — that crop
    // doesn't exist yet (see gcseScienceFigureSet comment above).
    const visualLookup = new Map();
    for (const q of paper.questions) {
      for (const v of q.visuals ?? []) {
        if (!v.visual_id || !v.file_path || visualLookup.has(v.visual_id)) continue;
        const basename = v.file_path.split('/').pop();
        if (!gcseScienceFigureSet.has(`${paper.subject}|${paper.paper_id}|${basename}`)) continue;
        visualLookup.set(v.visual_id, {
          fig_id:      v.visual_id,
          file_path:   rewriteSciencePath(v.file_path, paper.subject, paper.paper_id),
          alt_text:    v.alt_text ?? '',
          description: v.caption ?? v.alt_text ?? '',
        });
      }
    }

    // Quick question_number -> id mapping for the group -> leaf back-reference.
    const numberToId = new Map();
    for (const q of paper.questions) {
      if (q.question_number && q.id) numberToId.set(q.question_number, q.id);
    }

    for (const group of paper.groups) {
      const cardPath = gcseScienceCardMap.get(`${paper.subject}|${paper.paper_id}|${group.group_id}`);
      if (!cardPath) continue;

      const primary = {
        fig_id:           group.group_id,
        file_path:        cardPath,
        alt_text:         group.card_intro ?? group.shared_stimulus_text ?? '',
        description:      '',
        is_question_card: true,
      };

      const alternates = (group.alternate_visual_ids ?? [])
        .map((id) => visualLookup.get(id))
        .filter(Boolean);

      for (const qn of group.member_question_numbers ?? []) {
        const leafId = numberToId.get(qn);
        if (leafId) {
          leafIndex.set(leafId, { groupId: group.group_id, primary, alternates });
        }
      }
    }
  }

  return leafIndex;
}

const gcseSciencePaperIndex = buildSciencePaperIndex(gcsePaperCtx);

// Attach the group card + alternate visuals as the right-pane resource for
// any leaf the new pipeline grouped. Leaves outside the index fall back to
// the per-leaf visuals normalization above.
function attachScienceGroupCards(rows) {
  return rows.map((q) => {
    const entry = gcseSciencePaperIndex.get(q.id);
    if (!entry) return q;
    const { groupId, primary, alternates } = entry;
    return {
      ...q,
      group_id:           groupId,
      question_card_image: primary.file_path,
      resource: {
        id:   `${q.source_paper}-${q.question_number}-card`,
        type: 'multi-resource',
        figures: [primary, ...alternates],
      },
    };
  });
}

const scienceQuestionsBySubject = {};
for (const subj of SCIENCE_SUBJECTS) {
  scienceQuestionsBySubject[subj] = attachScienceGroupCards(
    normalizeScienceQuestions(allGcseQuestions.filter((q) => q.subject === subj)),
  );
}

export const gcseQuestions = {
  psychology: allGcseQuestions.filter((q) => q.subject === 'psychology'),
  biology:    scienceQuestionsBySubject.biology,
  chemistry:  scienceQuestionsBySubject.chemistry,
  physics:    scienceQuestionsBySubject.physics,
};
